import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRouter } from 'next/router';

import { useBreakpoint } from '@hooks/useBreakpoint';
import { DIRECTION_TYPES, SORT_TYPES } from '@definitions/artworks.types';

import { LabelButton, SortButton, SortContainer, SortOptionsContainer } from './sortFilterItem.styles';

export interface SortOption {
  name: string;
  value: string;
}

export interface SortItem {
  name: string;
  options: SortOption[];
}

export interface SortFilterItemProps {
  onChangeSort: ({ direction, sort }: { direction: DIRECTION_TYPES; sort: SORT_TYPES | null }) => void;
}

export const SortFilterItem = ({ onChangeSort }: SortFilterItemProps) => {
  const router = useRouter();
  const { isMobile } = useBreakpoint();
  const { sort: querySort, direction: queryDirection } = router.query as {
    sort: SORT_TYPES;
    direction: DIRECTION_TYPES;
  };
  const [direction, setDirection] = useState<DIRECTION_TYPES>(queryDirection || DIRECTION_TYPES.ASC);
  const [sort, setSort] = useState<SORT_TYPES | null>(querySort || null);
  const [isExpanded, setIsExpanded] = useState(!isMobile);

  const setIsActiveAndPropagate = ({
    sort = null,
    direction = DIRECTION_TYPES.ASC,
  }: {
    sort?: SORT_TYPES | null;
    direction?: DIRECTION_TYPES;
  }) => {
    setDirection(direction);
    setSort(sort);
    onChangeSort({ sort, direction });
  };

  const SORT_ITEM_DATA = {
    name: 'Sort',
    id: 'sortItem',
    options: [
      {
        name: 'Default',
        id: 'sortItem.default',
        isActive: !sort,
        onClick: () => setIsActiveAndPropagate({}),
      },
      {
        name: 'Size: Small to Large',
        id: 'sortItem.sizeSmallToLarge',
        isActive: sort === SORT_TYPES.SIZE && direction == DIRECTION_TYPES.ASC,
        onClick: () => setIsActiveAndPropagate({ sort: SORT_TYPES.SIZE, direction: DIRECTION_TYPES.ASC }),
      },
      {
        name: 'Size: Large to Small',
        id: 'sortItem.sizeLargeToSmall',
        isActive: sort === SORT_TYPES.SIZE && direction == DIRECTION_TYPES.DESC,
        onClick: () => setIsActiveAndPropagate({ sort: SORT_TYPES.SIZE, direction: DIRECTION_TYPES.DESC }),
      },
      {
        name: 'Price: Low to High',
        id: 'sortItem.priceLowToHigh',
        isActive: sort === SORT_TYPES.PRICE && direction == DIRECTION_TYPES.ASC,
        onClick: () => setIsActiveAndPropagate({ sort: SORT_TYPES.PRICE, direction: DIRECTION_TYPES.ASC }),
      },
      {
        name: 'Price: High to Low',
        id: 'sortItem.priceHighToLow',
        isActive: sort === SORT_TYPES.PRICE && direction == DIRECTION_TYPES.DESC,
        onClick: () => setIsActiveAndPropagate({ sort: SORT_TYPES.PRICE, direction: DIRECTION_TYPES.DESC }),
      },
    ],
  };

  return (
    <SortContainer key={SORT_ITEM_DATA.name}>
      <LabelButton isExpanded={isExpanded} onClick={() => isMobile && setIsExpanded((prevValue) => !prevValue)}>
        <FormattedMessage id={SORT_ITEM_DATA.id} defaultMessage={SORT_ITEM_DATA.name} />:
      </LabelButton>
      <SortOptionsContainer isExpanded={!isMobile || isExpanded}>
        {SORT_ITEM_DATA.options.map((option, index) => (
          <SortButton key={option.id} autoFocus={index === 0} onClick={option.onClick} active={option.isActive}>
            <FormattedMessage id={option.id} defaultMessage={option.name} />
          </SortButton>
        ))}
      </SortOptionsContainer>
    </SortContainer>
  );
};
